import { useCallback, useRef, useState } from 'react';

import { CloudUpload, UploadFile, Close } from '@mui/icons-material';
import { Box, Divider, Grid, Paper, Stack, Typography } from '@mui/material';

import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  step: {
    maxWidth: '587px'
  }
}));

type Instruction = {
  bold?: string;
  text?: string;
  suffix?: string;
  children?: Instruction[];
};

interface InstructionListProps {
  items: Instruction[];
}

const InstructionsPanel: React.FC = () => {
  const instructions: Instruction[] = [
    { bold: 'Creator' },
    {
      bold: 'Account',
      children: [
        {
          bold: 'For YouTube, either:',
          children: [
            {
              text: 'https://www.youtube.com/@jondoe',
              suffix: '(always with the @)'
            },
            { text: 'https://www.youtube.com/channel/UCchannelid' },
            { text: 'https://www.youtube.com/user/jondoe_channel_name' }
          ]
        },
        { bold: 'For Instagram: ', text: 'https://www.instagram.com/jondoe' },
        { bold: 'For TikTok: ', text: 'https://www.tiktok.com/@jondoe' }
      ]
    }
  ];

  const errors = [
    {
      bold: 'Missing Required Fields:',
      text: 'Ensure each row has a creator, account.'
    },
    {
      bold: 'Missing/Misspelled Required Header:',
      text: 'Ensure first row has specified headers (Creator, Account).'
    },
    {
      bold: 'Too Many Rows:',
      text: 'Your file exceeds the 500 account limit. Please split your file and try again.'
    },
    {
      bold: 'Empty File:',
      text: "Your file doesn't contain any data."
    }
  ];

  const InstructionList = ({ items }: InstructionListProps) => (
    <Box component="ul" pl={2}>
      {items.map((item, i) => (
        <Typography key={i} component="li" variant="body2" mb={0.25}>
          {item.bold && (
            <strong style={{ fontWeight: 500 }}>{item.bold}</strong>
          )}
          {item.text && item.text}
          {item.suffix && (
            <strong style={{ fontWeight: 500, marginLeft: 1 }}>
              {item.suffix}
            </strong>
          )}
          {item.children && <InstructionList items={item.children} />}
        </Typography>
      ))}
    </Box>
  );

  return (
    <Box>
      <Typography fontWeight={500} gutterBottom>
        Instructions
      </Typography>
      <Typography variant="body2" fontWeight={400}>
        Make sure your file includes the following headers:
      </Typography>
      <Box component="ul" pl={2} mb={0.5} mt={0.5}>
        <InstructionList items={instructions} />
      </Box>
      <Typography variant="body2" fontWeight={400}>
        <strong style={{ fontWeight: 500 }}>Accepted formats:</strong> .csv,
        .xlsx
      </Typography>
      <Typography variant="body2" fontWeight={400}>
        <strong style={{ fontWeight: 500 }}>Maximum:</strong> 500 accounts per
        upload
      </Typography>
      <Divider sx={{ my: 2 }} />
      <Typography fontWeight="500" gutterBottom>
        Possible errors and their solutions
      </Typography>
      <Typography variant="body2">
        To avoid upload issues, please check the following before submitting
        your file:
      </Typography>
      <Box component="ul" pl={2} mb={0.5} mt={0.5}>
        {errors.map((item, i) => (
          <Typography key={i} component="li" variant="body2">
            <strong style={{ fontWeight: 500 }}>{item.bold}</strong> {item.text}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

interface Step1Props {
  uploadedFile: File | null;
  onFileChange: (file: File | null) => void;
}

const Step1 = ({ uploadedFile, onFileChange }: Step1Props) => {
  const [fileError, setFileError] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { classes } = useStyles();

  const validateFileType = useCallback((file: File): boolean => {
    const allowedTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    const allowedExtensions = ['.csv', '.xlsx', '.xls'];

    const fileName = file.name.toLowerCase();
    const hasValidExtension = allowedExtensions.some((ext) =>
      fileName.endsWith(ext)
    );
    const hasValidMimeType = allowedTypes.includes(file.type);

    return hasValidExtension || hasValidMimeType;
  }, []);

  const handleFileUpload = useCallback(
    (files: FileList) => {
      const file = files[0];

      if (!validateFileType(file)) {
        setFileError(true);
        onFileChange(null);
        return;
      }

      setFileError(false);
      onFileChange(file);
    },
    [validateFileType]
  );

  const DragDropZone: React.FC = () => {
    const [isDragOver, setIsDragOver] = useState<boolean>(false);

    const handleDragOver = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const { files } = e.dataTransfer;
      if (files.length > 0) {
        handleFileUpload(files);
      }
    }, []);

    const handleFileInput = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const { files } = e.target;
        if (files && files.length > 0) {
          handleFileUpload(files);
        }
      },
      []
    );

    const renderContent = () => {
      if (fileError) {
        return (
          <>
            <Close sx={{ fontSize: 60 }} />
            <Typography variant="h6" fontWeight={400} color="text.secondary">
              Wrong file type, please insert a valid file
            </Typography>
          </>
        );
      }

      if (uploadedFile) {
        return (
          <>
            <CloudUpload sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="body2" color="text.primary" mb={1}>
              {uploadedFile.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={2}>
              Click to replace file or drag a new one
            </Typography>
          </>
        );
      }

      return (
        <>
          <UploadFile sx={{ fontSize: 35 }} />
          <Typography variant="h6" fontWeight={400} color="text.secondary">
            Drag file or Upload it from your computer
          </Typography>
        </>
      );
    };

    const triggerFileInput = useCallback(() => {
      fileInputRef.current?.click();
    }, []);

    return (
      <Paper
        elevation={0}
        sx={{
          height: '100%',
          border: '2px solid #ccc',
          borderRadius: 1,
          p: 4,
          textAlign: 'center',
          alignContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          backgroundColor: isDragOver ? '#f5f5f5' : 'transparent',
          '&:hover': {
            borderColor: '#1976d2',
            backgroundColor: '#f9f9f9'
          }
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={triggerFileInput}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          style={{ display: 'none' }}
          onChange={handleFileInput}
        />
        {renderContent()}
      </Paper>
    );
  };

  return (
    <Stack gap={2}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Box display="flex" gap={2} flexDirection="column" height="100%">
            <Typography fontSize={27} fontWeight={400} className={classes.step}>
              Upload a CSV or Excel file to add up to{' '}
              <strong style={{ fontWeight: 500 }}>500 creators channels</strong>{' '}
              to the platform.
            </Typography>
            <Box flex={1}>
              <DragDropZone />
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box display="flex" flexDirection="column">
            <InstructionsPanel />
          </Box>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default Step1;
